import * as React from "react";
import { AlertTriangle, FilePenLine, Link2, ShieldAlert, Unlink } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MatchCandidateResponse, ReviewQueueItem } from "@/types/contract-family";

import { AmbiguousCandidateCard } from "./AmbiguousCandidateCard";

interface Props {
  item: ReviewQueueItem | null;
  outcomeExplanation: string | null;
  pending: boolean;
  errorMessage: string | null;
  onAcceptCandidate: (item: ReviewQueueItem, candidate: MatchCandidateResponse) => void;
  onMarkOrphan: (item: ReviewQueueItem) => void;
  onChangeRole: (item: ReviewQueueItem) => void;
  onEditMetadata: (item: ReviewQueueItem) => void;
  onSendBackToReview: (item: ReviewQueueItem) => void;
}

export const AmbiguousReviewPanel: React.FC<Props> = ({
  item,
  outcomeExplanation,
  pending,
  errorMessage,
  onAcceptCandidate,
  onMarkOrphan,
  onChangeRole,
  onEditMetadata,
  onSendBackToReview,
}) => {
  if (!item) {
    return <div className="px-4 py-8 text-sm text-slate-500">Select a document to review.</div>;
  }

  return (
    <section aria-label="Review details" className="flex min-h-0 flex-col">
      <div className="border-b border-slate-200 px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-slate-950">{item.name?.trim() || item.document_id}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge variant="outline" className="px-1.5 py-0 text-[10px] uppercase">
                {item.role}
              </Badge>
              <Badge variant="traffic-amber" className="px-1.5 py-0 text-[10px]">
                {item.family_status}
              </Badge>
              <span className="font-mono text-[10px] text-slate-400">{item.document_id}</span>
            </div>
          </div>
          {errorMessage && <p className="text-xs text-red-700">{errorMessage}</p>}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {outcomeExplanation && (
          <div className="mx-4 mt-4 flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <AlertTriangle aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>{outcomeExplanation}</p>
          </div>
        )}

        {item.hidden_candidate_count > 0 && (
          <div className="mx-4 mt-4 flex items-center gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
            <ShieldAlert className="h-3.5 w-3.5" />
            Some candidate matches are hidden by permissions.
          </div>
        )}

        {item.candidates.length > 0 ? (
          <div className="mt-4 border-y border-slate-100">
            {item.candidates.map(candidate => (
              <AmbiguousCandidateCard
                key={candidate.candidate_master_document_id}
                candidate={candidate}
                pending={pending}
                onAccept={candidate => onAcceptCandidate(item, candidate)}
              />
            ))}
          </div>
        ) : (
          <div className="mx-4 mt-4 flex items-center gap-1.5 text-xs text-slate-500">
            <AlertTriangle className="h-3.5 w-3.5" />
            No visible candidate matches.
          </div>
        )}
      </div>

      <div className="flex flex-wrap justify-end gap-1.5 border-t border-slate-200 px-4 py-3">
        <Button type="button" size="xs" variant="outline" onClick={() => onMarkOrphan(item)} disabled={pending}>
          Orphan
        </Button>
        <Button type="button" size="xs" variant="outline" onClick={() => onChangeRole(item)} disabled={pending}>
          <Link2 className="mr-1 h-3.5 w-3.5" />
          Role
        </Button>
        <Button type="button" size="xs" variant="outline" onClick={() => onEditMetadata(item)} disabled={pending}>
          <FilePenLine className="mr-1 h-3.5 w-3.5" />
          Metadata
        </Button>
        <Button type="button" size="xs" variant="outline" onClick={() => onSendBackToReview(item)} disabled={pending}>
          <Unlink className="mr-1 h-3.5 w-3.5" />
          Send back to review
        </Button>
      </div>
    </section>
  );
};
